import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

import type { UsuarioLogadoOutputType } from '../../../shared/dto';
import { hasFeature, type FeatureKey } from '../../../shared/features';
import { authApi } from '../api/auth.api';
import { usuariosApi } from '../api/usuarios.api';
import { useApiLazyQuery, useApiMutation } from './use-api';

interface AuthContextValue {
  usuario: UsuarioLogadoOutputType | null;
  /** `true` só durante a checagem inicial (`GET /me` no boot) — nunca mais depois disso. */
  carregando: boolean;
  isAdmin: boolean;
  isSupervisor: boolean;
  /** Admin ou supervisor; espelha `requireAdminOuSupervisor` do backend (hoje só matrículas). */
  podeGerenciarMatriculas: boolean;
  /** Admin (qualquer um) ou o próprio professor/supervisor; espelha `requireAdmin`/`restrictProfessorSelf` do backend. */
  podeEditarProfessor: (professorId: string) => boolean;
  /** Só o professor dono escreve no registro (admin sem vínculo só lê); espelha `requireProfessor` + o escopo de escrita do backend. */
  podeEscreverRegistro: (professorId: string) => boolean;
  hasFeature: (key: FeatureKey) => boolean;
  login: (email: string, senha: string) => Promise<void>;
  logout: () => Promise<void>;
  atualizarNomeEmpresa: (nomeEmpresa: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Fonte única do usuário autenticado; a sessão é cookie `httpOnly`, nunca há token em JS. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioLogadoOutputType | null>(null);
  const [carregando, setCarregando] = useState(true);

  const { execute: verificarSessao } = useApiLazyQuery(authApi.me);
  // Silenciosas: `login` mostra erro inline e o logout limpa `usuario` local mesmo se falhar.
  const { mutate: autenticar } = useApiMutation(authApi.login, { silent: true });
  const { mutate: encerrarSessao } = useApiMutation(authApi.logout, { silent: true });
  const { mutate: salvarNomeEmpresa } = useApiMutation(usuariosApi.atualizarNomeEmpresa);

  useEffect(() => {
    let ativo = true;

    verificarSessao()
      .then((data) => {
        if (ativo) {
          setUsuario(data);
        }
      })
      .catch(() => {
        if (ativo) {
          setUsuario(null);
        }
      })
      .finally(() => {
        if (ativo) {
          setCarregando(false);
        }
      });

    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    const aoDeslogar = () => setUsuario(null);
    window.addEventListener('kflow:unauthorized', aoDeslogar);
    return () => window.removeEventListener('kflow:unauthorized', aoDeslogar);
  }, []);

  const login = useCallback(
    async (email: string, senha: string) => {
      const { usuario: usuarioLogado } = await autenticar({ email, senha });
      setUsuario(usuarioLogado);
    },
    [autenticar],
  );

  const logout = useCallback(async () => {
    await encerrarSessao();
    setUsuario(null);
  }, [encerrarSessao]);

  const atualizarNomeEmpresa = useCallback(
    async (nomeEmpresa: string) => {
      const configuracao = await salvarNomeEmpresa({ nomeEmpresa });
      setUsuario((atual) => (atual ? { ...atual, nomeEmpresa: configuracao.nomeEmpresa } : atual));
    },
    [salvarNomeEmpresa],
  );

  const isAdmin = usuario?.papel === 'ADMIN';
  const isSupervisor = usuario?.papel === 'SUPERVISOR';
  const podeGerenciarMatriculas = isAdmin || isSupervisor;

  const podeEditarProfessor = useCallback(
    (professorId: string) => isAdmin || usuario?.professorId === professorId,
    [isAdmin, usuario],
  );

  const podeEscreverRegistro = useCallback(
    (professorId: string) => usuario?.professorId === professorId,
    [usuario],
  );

  const verificarFeature = useCallback(
    (key: FeatureKey) => hasFeature(usuario?.features ?? [], key),
    [usuario],
  );

  return (
    <AuthContext.Provider
      value={{
        usuario,
        carregando,
        isAdmin,
        isSupervisor,
        podeGerenciarMatriculas,
        podeEditarProfessor,
        podeEscreverRegistro,
        hasFeature: verificarFeature,
        login,
        logout,
        atualizarNomeEmpresa,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  }
  return ctx;
}
